// useSaveEvent — the AddEventForm submit flow.
//
// Extracted verbatim from `components/AddEventForm.js` (behavior-preserving
// refactor, issue #240). This hook owns the `isSaving` flag and the
// `handleSave` action, preserving the EXACT order and awaited semantics the
// 13.1 parity baseline pins:
//
//   1. validate — `validateRequiredFields(eventType, formData)` builds the
//      per-type errors map (only the active type's required fields, no foreign
//      field leaks; unknown type → no required fields → empty map).
//   2. if the map is non-empty → `setErrors(errors)` and EARLY-RETURN. The save
//      is suppressed: `onAddEvent` does NOT fire and `isSaving` stays false,
//      exactly as the source gated on `Object.keys(errors).length > 0`.
//   3. otherwise → `setErrors({})`, then `setIsSaving(true)` (the Add button
//      switches to "Adding..." and disables).
//   4. build the payload via `processDateFields(formData, eventType)` and
//      `await onAddEvent(payload)` — validation always runs BEFORE onAddEvent.
//   5. on reject → `console.error('Error saving event:', error)`; the entered
//      values are RETAINED (the source neither resets the form nor closes on
//      success or failure), so the user can re-submit.
//   6. in `finally` → `setIsSaving(false)` (always re-enables the button).
//
// `eventType`, `formData`, `setErrors`, and `onAddEvent` are injected as params
// (`eventType`/`onAddEvent` are container props; `formData`/`setErrors` come
// from `useEventDraft`). The hook reuses the 13.2 logic modules
// (validateRequiredFields, processDateFields) rather than duplicating them.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useCallback } from 'react';

import validateRequiredFields from '../logic/validateRequiredFields';
import processDateFields from '../logic/processDateFields';

/**
 * Owns the submit flow: `isSaving` state and the validate → build → await
 * onAddEvent handler.
 *
 * @param {Object} params
 * @param {string} params.eventType - The active event type (selects required
 *   fields for validation and the payload conversions).
 * @param {Object} params.formData - The current draft values to validate and
 *   convert into the `onAddEvent` payload.
 * @param {(value: Object) => void} params.setErrors - Sets the validation-error
 *   map (from `useEventDraft`); called with the errors on a blank Save and with
 *   `{}` once validation passes.
 * @param {(payload: Object) => (Promise<*>|*)} params.onAddEvent - The persist
 *   callback, awaited after a successful validation.
 * @returns {{ isSaving: boolean, handleSave: () => Promise<void> }} The save API.
 */
export function useSaveEvent({ eventType, formData, setErrors, onAddEvent }) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    // Validate required fields before submission.
    const errors = validateRequiredFields(eventType, formData);

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setErrors({});
    setIsSaving(true);
    try {
      const processedData = processDateFields(formData, eventType);
      await onAddEvent(processedData);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setIsSaving(false);
    }
  }, [eventType, formData, setErrors, onAddEvent]);

  return { isSaving, handleSave };
}

export default useSaveEvent;
